import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState('');
  const [accessType, setAccessType] = useState('');
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');

  // Fetch permissions from API
  const fetchPermissions = async (userEmail) => {
    try {
      setLoading(true);
      const response = await axios.post(
        'https://server.majestyfx.com/admin_get_permissions.php',
        { email: userEmail }
      );

      if (response.data.success) {
        setPermissions(response.data.permissions || []);
        setRole(response.data.role || '');
        setAccessType(response.data.access_type || '');
        setEmail(userEmail);

        // Store in localStorage for persistence
        localStorage.setItem('userPermissions', JSON.stringify({
          permissions: response.data.permissions,
          role: response.data.role,
          accessType: response.data.access_type,
          email: userEmail
        }));
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Clear permissions on error
      setPermissions([]);
      setRole('');
      setAccessType('');
    } finally {
      setLoading(false);
    }
  };

  // Load permissions from localStorage on mount
  useEffect(() => {
    const storedPermissions = localStorage.getItem('userPermissions');
    const adminData = localStorage.getItem('adminData');

    if (storedPermissions) {
      const parsed = JSON.parse(storedPermissions);
      setPermissions(parsed.permissions || []);
      setRole(parsed.role || '');
      setAccessType(parsed.accessType || '');
      setEmail(parsed.email || '');
      setLoading(false);
    } else if (adminData) {
      const admin = JSON.parse(adminData);
      if (admin.email) {
        fetchPermissions(admin.email);
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Check if user has a specific permission
  const hasPermission = (permissionName) => {
    // If Administrator role or full access (subAdmin), grant all permissions
    if (role === 'Administrator' || accessType === 'subAdmin' || accessType === 'full') {
      return true;
    }

    // Check if permission exists in user's permissions
    return permissions.some(p => p.name === permissionName);
  };

  // Check if user has access to a module
  const hasModuleAccess = (moduleName) => {
    // If Administrator role or full access, grant all module access
    if (role === 'Administrator' || accessType === 'subAdmin' || accessType === 'full') {
      return true;
    }

    // Check if user has any permission for the module
    const hasAccess = permissions.some(p => p.module === moduleName);
    
    // Debug log
    console.log('hasModuleAccess check:', {
      moduleName,
      hasAccess,
      permissions: permissions.filter(p => p.module === moduleName),
      allPermissions: permissions
    });
    
    return hasAccess;
  };

  // Check if user can view a module
  const canView = (moduleName) => {
    if (role === 'Administrator' || accessType === 'subAdmin' || accessType === 'full') {
      return true;
    }

    return permissions.some(
      p => p.module === moduleName && p.name.includes('view')
    );
  };

  // Check if user can create in a module
  const canCreate = (moduleName) => {
    if (role === 'Administrator' || accessType === 'subAdmin' || accessType === 'full') {
      return true;
    }

    return permissions.some(
      p => p.module === moduleName && p.name.includes('create')
    );
  };

  // Check if user can edit in a module
  const canEdit = (moduleName) => {
    if (role === 'Administrator' || accessType === 'subAdmin' || accessType === 'full') {
      return true;
    }

    return permissions.some(
      p => p.module === moduleName && (p.name.includes('edit') || p.name.includes('manage'))
    );
  };

  // Check if user can delete in a module
  const canDelete = (moduleName) => {
    if (role === 'Administrator' || accessType === 'subAdmin' || accessType === 'full') {
      return true;
    }

    return permissions.some(
      p => p.module === moduleName && (p.name.includes('delete') || p.name.includes('manage'))
    );
  };

  // Clear permissions (on logout)
  const clearPermissions = () => {
    setPermissions([]);
    setRole('');
    setAccessType('');
    setEmail('');
    localStorage.removeItem('userPermissions');
  };

  const value = {
    permissions,
    role,
    accessType,
    email,
    loading,
    fetchPermissions,
    hasPermission,
    hasModuleAccess,
    canView,
    canCreate,
    canEdit,
    canDelete,
    clearPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
